/**
 * Process Flow Visualizer
 * Renders step-by-step process flow diagrams
 *
 * Sprint 33: Following OOA/OOD principles:
 * - Single Responsibility: Only process flow rendering
 * - Strategy Pattern: Different step types rendered differently
 */

import {
    ProcessFlowData,
    ProcessStepData,
    STEP_TYPE_COLORS,
    STEP_TYPE_ICONS,
} from '../topology-types';

/**
 * ProcessFlowVisualizer - Renders horizontal step-by-step flow diagrams
 */
export class ProcessFlowVisualizer {
    private container: HTMLElement | null = null;
    private handoffClickCallback: ((target: string) => void) | null = null;

    /**
     * Initialize with a container element
     */
    public initialize(container: HTMLElement): void {
        this.container = container;
    }

    /**
     * Register callback for handoff badge clicks
     */
    public onHandoffClick(callback: (target: string) => void): void {
        this.handoffClickCallback = callback;
    }

    /**
     * Render a process flow with its steps
     */
    public renderFlow(flow: ProcessFlowData, steps: ProcessStepData[]): void {
        if (!this.container) return;

        const sortedSteps = [...steps].sort((a, b) => a.stepNumber - b.stepNumber);

        this.container.innerHTML = `
            <div class="process-flow-viewer bg-white rounded-lg border p-4">
                <!-- Flow Header -->
                <div class="flex items-center justify-between mb-4">
                    <div>
                        <h3 class="text-lg font-bold text-gray-800">${this.escapeHtml(flow.name)}</h3>
                        <p class="text-sm text-gray-500">${this.escapeHtml(flow.description)}</p>
                    </div>
                    <div class="flex items-center gap-3">
                        <span class="px-2 py-1 text-xs rounded-full ${this.getStatusClasses(flow.status)}">
                            ${flow.status}
                        </span>
                        ${flow.ownerRole ? `<span class="text-xs text-gray-500">Owner: ${this.escapeHtml(flow.ownerRole)}</span>` : ''}
                        ${flow.totalSlaHours ? `<span class="text-xs text-gray-500">SLA: ${flow.totalSlaHours}h</span>` : ''}
                    </div>
                </div>

                <!-- Steps Flow -->
                <div class="flex items-start gap-2 overflow-x-auto pb-4">
                    ${sortedSteps.map((step, index) => this.renderStep(step, index, sortedSteps.length)).join('')}
                </div>

                <!-- Legend -->
                <div class="mt-4 pt-3 border-t flex flex-wrap gap-3">
                    ${Object.entries(STEP_TYPE_COLORS).map(([type, color]) => `
                        <span class="flex items-center gap-1 text-xs text-gray-600">
                            <span class="w-3 h-3 rounded-full inline-block" style="background-color: ${color}"></span>
                            ${type}
                        </span>
                    `).join('')}
                </div>
            </div>
        `;

        // Wire handoff badge click events
        if (this.handoffClickCallback) {
            this.container.querySelectorAll('.handoff-badge').forEach(badge => {
                badge.addEventListener('click', () => {
                    const target = (badge as HTMLElement).dataset.handoffTarget;
                    if (target && this.handoffClickCallback) {
                        this.handoffClickCallback(target);
                    }
                });
            });
        }
    }

    /**
     * Highlight a specific step
     */
    public highlightStep(stepId: string): void {
        if (!this.container) return;

        // Remove existing highlights
        this.container.querySelectorAll('.process-step').forEach(el => {
            el.classList.remove('ring-2', 'ring-blue-500');
        });

        // Add highlight to target
        const stepEl = this.container.querySelector(`[data-step-id="${stepId}"]`);
        if (stepEl) {
            stepEl.classList.add('ring-2', 'ring-blue-500');
            stepEl.scrollIntoView({ behavior: 'smooth', inline: 'center' });
        }
    }

    /**
     * Show infrastructure links for a step
     */
    public showInfrastructureLinks(stepId: string, steps: ProcessStepData[]): string[] {
        const step = steps.find(s => s.stepId === stepId);
        return step?.infrastructure || [];
    }

    // ======================
    // Private Render Methods
    // ======================

    private renderStep(step: ProcessStepData, index: number, totalSteps: number): string {
        const color = STEP_TYPE_COLORS[step.stepType] || '#6b7280';
        const icon = STEP_TYPE_ICONS[step.stepType] || 'circle';
        const isLast = index === totalSteps - 1;
        const isDecision = step.stepType === 'Decision';

        return `
            <div class="flex items-center">
                <!-- Step Card -->
                <div class="process-step flex-shrink-0 w-48 rounded-lg border-2 p-3 hover:shadow-md transition-shadow"
                     data-step-id="${step.stepId}"
                     style="border-color: ${color}">

                    <!-- Step Header -->
                    <div class="flex items-center gap-2 mb-2">
                        <span class="flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold"
                              style="background-color: ${color}">
                            ${step.stepNumber}
                        </span>
                        <i data-feather="${icon}" class="w-4 h-4" style="color: ${color}"></i>
                    </div>

                    <!-- Step Name -->
                    <p class="text-sm font-semibold text-gray-800 mb-1">${this.escapeHtml(step.name)}</p>

                    <!-- Step Details -->
                    <p class="text-xs text-gray-500 mb-2">${this.escapeHtml(step.description).substring(0, 80)}${step.description.length > 80 ? '...' : ''}</p>

                    <!-- Role & System -->
                    <div class="space-y-1">
                        <div class="flex items-center gap-1">
                            <span class="text-xs text-gray-400">Role:</span>
                            <span class="text-xs font-medium text-gray-700">${this.escapeHtml(step.role)}</span>
                        </div>
                        ${step.system ? `
                            <div class="flex items-center gap-1">
                                <span class="text-xs text-gray-400">System:</span>
                                <span class="text-xs font-medium text-blue-600">${this.escapeHtml(step.system)}</span>
                            </div>
                        ` : ''}
                        ${step.slaHours ? `
                            <div class="flex items-center gap-1">
                                <span class="text-xs text-gray-400">SLA:</span>
                                <span class="text-xs font-medium">${step.slaHours}h</span>
                            </div>
                        ` : ''}
                    </div>

                    <!-- Infrastructure Links -->
                    ${step.infrastructure.length > 0 ? `
                        <div class="mt-2 pt-2 border-t">
                            <span class="text-xs text-gray-400">Infrastructure:</span>
                            <div class="flex flex-wrap gap-1 mt-1">
                                ${step.infrastructure.map(infra => `
                                    <span class="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">${this.escapeHtml(infra)}</span>
                                `).join('')}
                            </div>
                        </div>
                    ` : ''}

                    <!-- Decision Options -->
                    ${isDecision && step.decisionOptions.length > 0 ? `
                        <div class="mt-2 pt-2 border-t">
                            <span class="text-xs text-gray-400">Outcomes:</span>
                            <div class="space-y-1 mt-1">
                                ${step.decisionOptions.map(([label, _target]) => `
                                    <span class="block px-1.5 py-0.5 text-xs bg-amber-50 text-amber-700 rounded">${this.escapeHtml(label)}</span>
                                `).join('')}
                            </div>
                        </div>
                    ` : ''}

                    <!-- Handoff Badges -->
                    ${step.metadata.handoff_to ? `
                        <div class="mt-2 pt-2 border-t">
                            <span class="handoff-badge cursor-pointer inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                                  data-handoff-target="${this.escapeHtml(step.metadata.handoff_to)}">
                                <span>→</span> ${this.escapeHtml(step.metadata.handoff_label || step.metadata.handoff_to)}
                            </span>
                        </div>
                    ` : ''}
                    ${step.metadata.receive_from ? `
                        <div class="mt-2 pt-2 border-t">
                            <span class="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded-full">
                                <span>←</span> ${this.escapeHtml(step.metadata.receive_from_label || step.metadata.receive_from)}
                            </span>
                        </div>
                    ` : ''}
                </div>

                <!-- Arrow -->
                ${!isLast ? `
                    <div class="flex-shrink-0 px-1">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                    </div>
                ` : ''}
            </div>
        `;
    }

    private getStatusClasses(status: string): string {
        switch (status) {
            case 'Active': return 'bg-green-100 text-green-800';
            case 'Draft': return 'bg-yellow-100 text-yellow-800';
            case 'Archived': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    }

    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}
